// @ts-check

/**
 * @file Renders the invoice line create/edit form.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import {router, useLocalSearchParams} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import buildRoutePath from "../../shared/build-route-path"
import Button from "../../components/button"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormTextInput from "../../components/form/form-text-input"
import {InvoiceLine} from "../../frontend-models/invoice-line"
import {optionalRouteParam, requiredRouteParam} from "../../shared/route-params"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} InvoiceLinesEditScreenProps */

/**
 * @typedef {object} InvoiceLinesEditScreenState
 * @property {boolean} loading Whether invoice line data is loading.
 * @property {InvoiceLine | null} invoiceLine Loaded invoice line being edited.
 * @property {boolean} saving Whether form submission is running.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Invoice line edit screen.
 * @augments {ShapeComponent<InvoiceLinesEditScreenProps, InvoiceLinesEditScreenState>}
 */
class InvoiceLinesEditScreen extends ShapeComponent {
  /** @type {string | null} */
  invoiceID
  /** @type {string | null} */
  invoiceLineID
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {InvoiceLinesEditScreenState} */
  state = {
    loading: true,
    invoiceLine: /** @type {InvoiceLine | null} */ null,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null
  }

  /** Initializes route params and invoice line loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()

    const invoiceLineID = optionalRouteParam(params, "id", "Invoice line ID")
    const invoiceID = invoiceLineID
      ? optionalRouteParam(params, "invoice_id", "Invoice ID")
      : requiredRouteParam(params, "invoice_id", "Invoice ID")
    this.invoiceID = invoiceID
    this.invoiceLineID = invoiceLineID
    this.params = params
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          if (invoiceLineID) {
            const invoiceLine = await InvoiceLine.find(invoiceLineID)
            if (cancelled) return
            this.s.loading = false
            this.s.invoiceLine = invoiceLine
          } else {
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [invoiceLineID])
  }

  /**
   * Renders the invoice line form.
   * @returns {ReactNode} Invoice line form content.
   */
  render() {
    const {loading, invoiceLine, saving, status} = this.s
    const {invoiceLineID} = this.tt
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="invoiceLineEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoiceLineEditScreen"
      >
        <View
          testID="invoiceLineEditScreen/content"
        >
          <Text
            style={styles.screenTitle ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="invoiceLineEditScreen/title"
          >
            {invoiceLineID ? _("Edit invoice line") : _("New invoice line")}
          </Text>
          <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
            <Card
              style={styles.formCard ||= {
                gap: 12,
                maxWidth: 520,
                width: "100%"
              }}
              testID="invoiceLineEditScreen/form"
            >
              <FormTextInput
                defaultValue={invoiceLine ? invoiceLine.title() || "" : ""}
                label={_("Title")}
                name="title"
                testID="invoiceLineEditScreen/titleInput"
              />
              <FormTextInput
                defaultValue={
                  invoiceLine ? String(invoiceLine.quantity() ?? "") : ""
                }
                keyboardType="numeric"
                label={_("Quantity")}
                name="quantity"
                testID="invoiceLineEditScreen/quantityInput"
              />
              <FormTextInput
                defaultValue={
                  invoiceLine && invoiceLine.amountCents() != null
                    ? String(Number(invoiceLine.amountCents()) / 100)
                    : ""
                }
                keyboardType="numeric"
                label={_("Amount")}
                name="amount"
                testID="invoiceLineEditScreen/amountInput"
              />
              <FormTextInput
                defaultValue={
                  invoiceLine ? invoiceLine.amountCurrency() || "DKK" : "DKK"
                }
                label={_("Amount currency")}
                name="amountCurrency"
                testID="invoiceLineEditScreen/amountCurrencyInput"
              />
              {status &&
                <Text
                  style={styles[`status-${themeName}-${status.kind}`] ||= {
                    color: status.kind === "success" ? colors.successText : colors.dangerText,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="invoiceLineEditScreen/statusMessage"
                >
                  {status.text}
                </Text>
              }
              <Button
                disabled={saving}
                iconName="floppy-disk"
                label={saving ? _("Saving...") : _("Save")}
                onPress={this.tt.onSubmit}
                testID="invoiceLineEditScreen/saveButton"
              />
            </Card>
          </Form>
        </View>
      </View>
    )
  }

  /**
   * Saves the invoice line form.
   * @returns {Promise<void>} Resolves when save handling completes.
   * @rejects {unknown} When save handling fails.
   */
  onSubmit = async () => {
    const {invoiceID, invoiceLineID, params} = this.tt
    const {invoiceLine} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    if (invoiceLineID && !invoiceLine) {
      this.s.status = {
        kind: "error",
        text: _("Invoice line not found")
      }
      return
    }
    this.s.saving = true
    this.s.status = null
    try {
      const amountValue = parseFloat(values.amount || "0")
      const attributes = {
        amountCents: Number.isFinite(amountValue)
          ? Math.round(amountValue * 100)
          : 0,
        amountCurrency: values.amountCurrency || "DKK",
        quantity: values.quantity || "",
        title: values.title || ""
      }
      let targetInvoiceId = ""
      if (invoiceLine) {
        await invoiceLine.update(attributes)
        targetInvoiceId = String(invoiceLine.invoiceId() || "")
      } else {
        if (!invoiceID || !/^\d+$/.test(invoiceID)) throw new Error(_("Invoice not found"))

        const created = await InvoiceLine.create({
          ...attributes,
          invoiceId: invoiceID
        })
        targetInvoiceId = String(created.invoiceId() || invoiceID)
      }
      if (targetInvoiceId) {
        router.push(buildRoutePath(params, `/invoices/${targetInvoiceId}`))
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<InvoiceLinesEditScreenProps>} */
const InvoiceLinesEditScreenComponent = memo(shapeComponent(InvoiceLinesEditScreen))

export default InvoiceLinesEditScreenComponent
